import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';

import { ChannelOption, ChannelId } from '@/pages/PersonMessaging/types';
import MessageRepository from '@/repositories/MessageRepository';
import { Channel } from '@/types/channel';

const CHANNEL_LABELS: Record<ChannelId, string> = {
  [Channel.SMS]: 'SMS',
  [Channel.EMAIL]: 'Email',
  [Channel.VOICE]: 'Voice'
};

export const useMessageSenders = ({
  hasPhone,
  hasEmail
}: {
  hasPhone: boolean;
  hasEmail: boolean;
}) => {
  const messageRepository = MessageRepository();
  const select = useCallback(
    (senders: { channel: string; sender: string; name?: string }[]) => {
      const channelIndex: Record<string, number> = {};
      const options: ChannelOption[] = [];

      for (const { channel, sender, name } of senders) {
        if (channel === Channel.SMS && !hasPhone) continue;
        if (channel === Channel.EMAIL && !hasEmail) continue;
        if (channel === Channel.VOICE && !hasPhone) continue;

        const id = channel as ChannelId;
        const count = channelIndex[channel] ?? 0;
        channelIndex[channel] = count + 1;

        const value = channel === Channel.EMAIL ? channel : `${channel}-${count}`;
        options.push({
          id,
          label: CHANNEL_LABELS[id] ?? channel,
          sender,
          value,
          name
        });
      }

      return options;
    },
    [hasPhone, hasEmail]
  );

  return useQuery({
    queryKey: ['messageSenders'],
    queryFn: () => messageRepository.getSenders(),
    select,
    staleTime: 5 * 60 * 1000
  });
};
